import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ICatalogueCustomization } from "../../../../apis/useGetCatalogueData/types";
import { globalEvents } from "../../../../common/helpers/GlobalEvents";
import { Nullable } from "../../../../common/types/types";
import { PDP_EVENT } from "../../constants";
import { ICustomizationSetItem, IPricedAccordionSelection } from "../setitems/types";

// Optimized price diff calculation with Map for O(1) lookup
// Note: Uses first matching price_diff for each ID, matching original behavior
const createPriceDiffCalculator = (selections: IPricedAccordionSelection[]) => {
	const priceMap = new Map<string, number>();

	// Store only the first occurrence of each ID to match original filter behavior
	for (const { id, price_diff } of selections) {
		if (!priceMap.has(id)) {
			priceMap.set(id, price_diff ?? 0);
		}
	}

	return (selected: string[]): number => {
		return selected.reduce<number>((diff, select) => {
			return diff + (priceMap.get(select) ?? 0);
		}, 0);
	};
};

export const useCustomizationChangeHandler = (
	idx: number,
	customization: ICatalogueCustomization,
	setitem?: Nullable<ICustomizationSetItem>,
	selectedCustomization?: ICatalogueCustomization | null
) => {
	const [selection, setSelection] = useState<string[]>([]);
	const initializationRef = useRef<string | null>(null);
	const mountedRef = useRef<boolean>(true);

	// Reset on mount
	useEffect(() => {
		mountedRef.current = true;
		initializationRef.current = null;

		return () => {
			mountedRef.current = false;
		};
	}, [idx, customization.id]);

	// Memoize the selected IDs to prevent unnecessary re-renders
	const selectedIds = useMemo(() => {
		if (selectedCustomization?.food_supplier_customization_options) {
			return selectedCustomization.food_supplier_customization_options.map((option) =>
				String(option.id ?? "")
			);
		}

		return [];
	}, [
		selectedCustomization?.id,
		selectedCustomization?.food_supplier_customization_options?.length,
	]);

	// Initialize selection from selectedCustomization
	useEffect(() => {
		if (!mountedRef.current) return;

		const idsKey = selectedIds.join(",");

		if (selectedIds.length > 0 && initializationRef.current !== idsKey) {
			setSelection(selectedIds);
			initializationRef.current = idsKey;
		} else if (selectedIds.length === 0 && initializationRef.current !== null) {
			setSelection([]);
			initializationRef.current = null;
		}
	}, [selectedIds]);

	const onChange = useCallback(
		(selections: IPricedAccordionSelection[]) => {
			const getPriceDiff = createPriceDiffCalculator(selections);

			const fn = (selected: string[]) => {
				const updateevent = setitem
					? PDP_EVENT.UPDATE_SETITEM_CUSTOMIZATION
					: PDP_EVENT.UPDATE_CUSTOMIZATION;
				const price_diff = getPriceDiff(selected);

				globalEvents.emit(updateevent, { idx, customization, selected, setitem, price_diff });
				setSelection(selected);
			};

			return fn;
		},
		[idx, customization, setitem]
	);

	const withinQty = useMemo(() => {
		const { min_permitted, max_permitted } = customization;

		if (selection.length < min_permitted) return false;
		if (selection.length > max_permitted) return false;

		return true;
	}, [customization, selection]);

	return {
		onChange,
		withinQty,
		selection,
	};
};
